import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE } from './siteMeta';

/**
 * Page-level SEO component. Wraps react-helmet-async with sensible defaults.
 *
 * Props:
 *   title, description    — required
 *   canonical             — relative path (e.g. "/services/plumbing")
 *   image                 — Open Graph image URL (absolute)
 *   jsonLd                — single object or array of JSON-LD schema objects
 *   keywords              — comma-separated string (optional)
 */
const Seo = ({ title, description, canonical = '/', image, jsonLd, keywords }) => {
  const fullUrl = `${SITE.url}${canonical}`;
  const ogImage = image || `${SITE.url}${SITE.logo}`;
  const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Robots */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />

      {/* JSON-LD */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
